import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Stack,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material'
import { AutoAwesome as AiIcon, Group as GroupIcon } from '@mui/icons-material'
import {
  ExistingDrugGroup,
  AiGenerationRequest,
} from '@/features/internal/drugs/types/soapTemplate'

interface DuplicateConfirmationDialogProps {
  open: boolean
  existingGroups: ExistingDrugGroup[]
  originalRequests: AiGenerationRequest[]
  loading?: boolean
  onMerge: () => void
  onRename: (newRequests: AiGenerationRequest[]) => void
  onCancel: () => void
}

const DuplicateConfirmationDialog: React.FC<
  DuplicateConfirmationDialogProps
> = ({
  open,
  existingGroups,
  originalRequests,
  loading = false,
  onMerge,
  onRename,
  onCancel,
}) => {
  const [renameMode, setRenameMode] = useState(false)
  const [renamedRequests, setRenamedRequests] = useState(originalRequests)

  // リネームモードに切り替え時の自動提案
  const handleRename = () => {
    const newRequests = originalRequests.map((req) => {
      const isDuplicate = existingGroups.some(
        (group) => group.name === req.drug_group_name
      )
      if (isDuplicate) {
        const baseName = req.drug_group_name
        let suffix = 1
        let newName = `${baseName}（AI生成）`

        // 既存グループと名前が重複しないまで番号を増やす
        while (existingGroups.some((group) => group.name === newName)) {
          suffix++
          newName = `${baseName}（AI生成${suffix}）`
        }

        return {
          ...req,
          drug_group_name: newName,
        }
      }
      return req
    })
    setRenamedRequests(newRequests)
    setRenameMode(true)
  }

  // リネーム入力の更新
  const updateRenamedRequest = (index: number, newName: string) => {
    const updated = [...renamedRequests]
    updated[index] = { ...updated[index], drug_group_name: newName }
    setRenamedRequests(updated)
  }

  // リネーム実行
  const handleConfirmRename = () => {
    onRename(renamedRequests)
  }

  // ダイアログを閉じる際のリセット
  const handleClose = () => {
    setRenameMode(false)
    setRenamedRequests(originalRequests)
    onCancel()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
        }}
      >
        <Typography variant="h6" component="span">
          グループ名の重複
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }}>
          既存グループに追加するか、別名で新規作成してください。
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            <GroupIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            既存のグループ
          </Typography>
          <Stack spacing={2}>
            {existingGroups.map((group) => (
              <Card key={group.id} variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ py: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 1,
                    }}
                  >
                    <Typography variant="h6">{group.name}</Typography>
                    <Stack direction="row" spacing={1}>
                      <Chip
                        label={`${group.template_count}件`}
                        size="small"
                        variant="outlined"
                        sx={{
                          color: 'text.primary',
                          borderColor: 'text.secondary',
                        }}
                      />
                      {group.has_ai_templates && (
                        <Chip
                          label="AI生成含む"
                          size="small"
                          variant="outlined"
                          icon={<AiIcon sx={{ fontSize: 16 }} />}
                          sx={{
                            color: 'text.primary',
                            borderColor: 'text.secondary',
                          }}
                        />
                      )}
                    </Stack>
                  </Box>
                  {group.description && (
                    <Typography variant="body2" color="text.secondary">
                      {group.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Box>

        {renameMode && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                新しいグループ名
              </Typography>
              <Stack spacing={2}>
                {renamedRequests.map((req, index) => (
                  <Box key={index}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      グループ {index + 1}: {req.purpose_description}
                    </Typography>
                    <TextField
                      fullWidth
                      value={req.drug_group_name}
                      onChange={(e) =>
                        updateRenamedRequest(index, e.target.value)
                      }
                      placeholder="新しいグループ名を入力"
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                ))}
              </Stack>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        {!renameMode ? (
          <>
            <Button
              onClick={handleClose}
              sx={{
                color: 'text.secondary',
              }}
            >
              キャンセル
            </Button>
            <Button
              onClick={handleRename}
              variant="outlined"
              disabled={loading}
              sx={{
                mr: 1,
                color: 'text.primary',
                borderColor: 'text.secondary',
                '&:hover': {
                  borderColor: 'text.primary',
                  backgroundColor: 'action.hover',
                },
              }}
            >
              別名で新規作成
            </Button>
            <Button
              onClick={onMerge}
              variant="contained"
              disabled={loading}
              startIcon={
                loading ? <CircularProgress size={20} color="inherit" /> : null
              }
              sx={{
                '&:disabled': {
                  backgroundColor: 'action.disabled',
                  color: 'text.disabled',
                },
              }}
            >
              {loading ? '追加中...' : '既存グループに追加'}
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={() => setRenameMode(false)}
              sx={{
                color: 'text.secondary',
              }}
            >
              戻る
            </Button>
            <Button
              onClick={handleConfirmRename}
              variant="contained"
              disabled={
                loading ||
                renamedRequests.some((req) => !req.drug_group_name.trim())
              }
              startIcon={
                loading ? <CircularProgress size={20} color="inherit" /> : null
              }
              sx={{
                '&:disabled': {
                  backgroundColor: 'action.disabled',
                  color: 'text.disabled',
                },
              }}
            >
              {loading ? '作成中...' : 'この名前で作成'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default DuplicateConfirmationDialog
